import React, { useEffect } from "react";
import { useL } from "@/lib/useL";
import { X, Sparkles, MessageSquare, CheckCircle2 } from "lucide-react";

/**
 * Modal shown right after a user copies a "prompt" card from the
 * /enseignants/cas-pratiques page. The clipboard already holds the
 * prompt text — this modal explains what to do next and offers two
 * one-click paths:
 *   1. "Tester dans SUX1"   → pre-fills the Dashboard with the prompt
 *   2. "Tester dans ChatGPT" → opens chat.openai.com in a new tab
 *
 * Tri-color palette (blue / white / red) — no black blocks.
 */
export default function PromptCopiedModal({
  open,
  onClose,
  cas,
  isAuthenticated = false,
  onTestInSux1,
  onTestInChatGPT,
}) {
  const L = useL();
  // Close on ESC key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !cas) return null;

  const titleId = `prompt-copied-title-${cas.id}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#002FA7]/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="prompt-copied-modal-overlay"
    >
      <div
        className="relative w-full max-w-lg bg-background border-2 border-[#002FA7]"
        onClick={(e) => e.stopPropagation()}
        data-testid="prompt-copied-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Blue header */}
        <div className="bg-[#002FA7] text-white px-6 py-5 flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-white/70 mb-1">
              {L("Cas", "Case")} #{String(cas.id).padStart(2, "0")} — {L("prompt copié", "prompt copied")}
            </div>
            <h3 id={titleId} className="text-lg font-bold leading-snug">
              {cas.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="prompt-copied-close"
            className="shrink-0 text-white/80 hover:text-white transition-colors"
            aria-label={L("Fermer", "Close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Prompt preview */}
          <div className="border-l-2 border-[#FF2A00] pl-4 mb-6">
            <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-2">
              {L("Contenu copié dans votre presse-papier", "Content copied to your clipboard")}
            </div>
            <p className="text-sm text-foreground leading-relaxed line-clamp-4">
              {cas.prompt}
            </p>
          </div>

          <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-4">
            {L("Que voulez-vous faire ?", "What do you want to do?")}
          </div>

          {/* Primary CTA — SUX1 */}
          <button
            type="button"
            onClick={onTestInSux1}
            data-testid="prompt-copied-test-sux1"
            className="w-full flex items-center justify-between gap-3 bg-[#002FA7] text-white px-4 py-3 hover:bg-[#FF2A00] transition-colors mb-3 group"
          >
            <span className="flex items-center gap-3">
              <Sparkles className="w-4 h-4" />
              <span className="text-left">
                <span className="block font-mono uppercase tracking-[0.14em] text-[11px]">
                  {L("Tester dans SUX1", "Try in SUX1")}
                </span>
                <span className="block text-[11px] text-white/80 mt-0.5 normal-case tracking-normal">
                  {isAuthenticated
                    ? L("Prompt pré-rempli · coûte comme un résumé", "Prompt pre-filled · costs like a summary")
                    : L("Compte requis · dès 2 € les 20 résumés", "Account required · from €2 for 20 summaries")}
                </span>
              </span>
            </span>
            <span className="font-mono text-[11px] opacity-70 group-hover:opacity-100">
              →
            </span>
          </button>

          {/* Secondary CTA — ChatGPT */}
          <button
            type="button"
            onClick={onTestInChatGPT}
            data-testid="prompt-copied-test-chatgpt"
            className="w-full flex items-center justify-between gap-3 bg-background text-[#002FA7] border border-[#002FA7] px-4 py-3 hover:bg-[#002FA7] hover:text-white transition-colors group"
          >
            <span className="flex items-center gap-3">
              <MessageSquare className="w-4 h-4" />
              <span className="text-left">
                <span className="block font-mono uppercase tracking-[0.14em] text-[11px]">
                  {L("Tester dans ChatGPT", "Try in ChatGPT")}
                </span>
                <span className="block text-[11px] text-muted-foreground group-hover:text-white/80 mt-0.5 normal-case tracking-normal">
                  {L("Ouvre chat.openai.com · collez avec Ctrl+V", "Opens chat.openai.com · paste with Ctrl+V")}
                </span>
              </span>
            </span>
            <span className="font-mono text-[11px] opacity-70 group-hover:opacity-100">
              ↗
            </span>
          </button>

          {/* Helper text */}
          <p className="text-[11px] text-muted-foreground text-center mt-5 leading-relaxed">
            {L("Le prompt reste dans votre presse-papier.", "The prompt stays in your clipboard.")}<br />
            {L("Vous pouvez aussi le coller dans Claude, Gemini ou n'importe quel outil IA.", "You can also paste it into Claude, Gemini or any AI tool.")}
          </p>
        </div>
      </div>
    </div>
  );
}
